import type { GuestBrowserCapability, ITuningClock, ResolvedTuningConfiguration, TuningIdentity, TuningTrialControlContext, TuningWireMessage } from '@playcastdotio/PeerCore';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { ClientPeerManager } from '../../../api/modules/ClientPeerManager';
import { EncodedFrameStatsTap } from '../../../api/modules/EncodedFrameStatsTap';
import { guestStatsSnapshotService } from '../telemetry/GuestStatsSnapshotService';
import { GuestTrialBinding } from './GuestTrialBinding';
import { GuestTrialInputBinding } from './GuestTrialInputBinding';
import { streamTuningService } from '../services/StreamTuningService';

type Options = { eligible: boolean; buildId: string; runtimeId: string; clock: ITuningClock;
  peerId: () => string; hostId: () => string; isMobile: boolean; capability?: GuestBrowserCapability;
  manager: () => ClientPeerManager | undefined; discardManager: () => void;
  resetMedia: () => void; cancelRecovery: () => void; signalingReady: () => Promise<void>;
  connectFresh: (configuration: ResolvedTuningConfiguration) => Promise<void>;
  firstFrameReady: () => boolean;
  fallback: (message: TuningWireMessage, context: TuningTrialControlContext) => Promise<void>;
  neutralizeGamepad: () => boolean };

/** Thin application composition; does not reclaim identities or reload the page. */
export class GuestTrialApplication {
  readonly binding: GuestTrialBinding;
  private readonly input: GuestTrialInputBinding;
  private unsubscribeTiming?: () => void;
  private tap?: EncodedFrameStatsTap;
  private readonly connectionListeners = new Set<() => void>();
  constructor(private readonly options: Options) {
    this.input = new GuestTrialInputBinding({ hostId: options.hostId,
      forwardRelease: message => options.manager()?.send(JSON.stringify(message)) ?? false, neutralizeGamepad: options.neutralizeGamepad });
    this.binding = new GuestTrialBinding({ ...options, localPeerId: options.peerId,
      sampling: interval => options.manager()?.configureTuningSampling(interval),
      media: {
        suspendInput: async target => { this.input.suspend(target); await this.input.neutralize(target); },
        closeCurrent: async () => { options.manager()?.close('player:close:tuning-replacement'); options.discardManager(); },
        discardPrewarm: () => undefined, cancelRecovery: options.cancelRecovery,
        reset: async (target, configuration) => {
          this.binding.state.reset(target, configuration); this.binding.negotiation.configure(configuration); options.resetMedia();
        }, signalingReady: options.signalingReady,
        connect: async (_target, configuration) => { await options.connectFresh(configuration); await this.waitConnected(); return this.binding.endpointEpoch(); },
        firstFrame: (target, epoch, start) => this.binding.firstFrame(target, epoch, start),
        receipts: () => this.binding.settingReceipts(), resumeInput: target => this.input.resume(target),
        abort: async () => { options.manager()?.close('player:close:tuning-abort'); options.discardManager(); },
      } });
    if (options.eligible) streamTuningService.bind(this.binding);
  }
  hooks(): ReturnType<GuestTrialBinding['hooks']> { return this.options.eligible ? this.binding.hooks() : {}; }
  connected(): void { for (const listener of this.connectionListeners) listener(); }
  bindStats(manager: ClientPeerManager): void {
    guestStatsSnapshotService.bind({ latest: () => manager.latestStats(), subscribe: listener => manager.subscribeStats(listener) });
  }
  encodedTap(): EncodedFrameStatsTap | undefined {
    this.unsubscribeTiming?.(); this.unsubscribeTiming = undefined;
    this.tap?.dispose(); this.tap = undefined;
    const enabled = this.binding.state.armed() ? this.binding.state.snapshot().effective['telemetry.encodedFrameTap'] !== false : true;
    if (!enabled || !EncodedFrameStatsTap.isSupported()) return undefined;
    const tap = new EncodedFrameStatsTap(); this.tap = tap;
    if (this.options.eligible) this.unsubscribeTiming = tap.subscribeTiming(batch => this.binding.telemetry.observeEncodedBatch(batch));
    return tap;
  }
  allows(message: PlaycastMessage<PlaycastMessageTarget>): boolean { return !this.options.eligible || this.input.allows(this.options.peerId(), message); }
  forwarded(message: PlaycastMessage<PlaycastMessageTarget>): void { if (this.options.eligible) this.input.observeForwarded(this.options.peerId(), message); }
  dispose(target: TuningIdentity | undefined): void {
    this.unsubscribeTiming?.(); this.tap?.dispose(); this.tap = undefined; this.binding.dispose(); guestStatsSnapshotService.bind(null);
    if (this.options.eligible) streamTuningService.bind(null);
    if (target) this.input.dispose(target);
  }
  private waitConnected(): Promise<void> {
    if (this.options.manager()?.isConnected()) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const done = () => { clearTimeout(timer); this.connectionListeners.delete(done); resolve(); };
      const timer = setTimeout(() => { this.connectionListeners.delete(done); reject(new Error('Fresh guest connection did not establish')); }, 10_000);
      this.connectionListeners.add(done);
    });
  }
}
